/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Label,
  Legend,
  Pie,
  PieChart,
  ReferenceDot,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Chip } from "../../components/Chip";
import { Skeleton } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGear } from "@fortawesome/free-solid-svg-icons";
import { DateRangePickerCalendar } from "../../components/DateRangePicker";
import localforage from "localforage";
import { onRTAReconnect, paymentAPI, RTA } from "../..";
import { AxiosRequestConfig } from "axios";
import {
  AutoGraphRounded,
  BarChartRounded,
  DataUsageRounded,
  ErrorOutlineRounded,
} from "@mui/icons-material";
import { Title } from "../../components/Title";
import { useUser } from "../../components/UserContext";
import { PainelPageAmary } from "../../components/tutorial/tutorialPage/painel";
import { capitalize, sessionInfo } from "../../utils/utils";
import { formatCurrencyBRL } from "../../utils/formatCurrencyBRL";
import { resolveFinancialGraphState } from "./financialGraphState";
import { buildCostBreakdown } from "./costBreakdown";
import { dashboardRangeParams } from "./dashboardRange";
import { DashboardInvalidation, DashboardRefreshDeduper } from "./dashboardRefresh";
import {
  parseGrossBillableBreakdown,
  parseGrossBillableOverview,
} from "./dashboardFinancialProjection";
import "./painel.css";

interface BasicRangeDate {
  startDate?: string;
  endDate?: string;
  refreshRevision?: number;
}

interface RangeWithGroup extends BasicRangeDate {
  groupBy?: "day" | "month" | "year";
}

const NoDataGraph: React.FC<{ label: string; icon: React.ReactNode }> = (props) => {
  return (
    <div className="noDataGraphContainer">
      <span className="noDataGraphText">{props.label}</span>
      {props.icon}
    </div>
  );
};

const Graph: React.FC<{
  children: React.ReactNode;
  onChange: (startDate: Date, endDate: Date) => void;
  title?: string;
  style?: React.CSSProperties;
}> = ({ children, title, style, onChange }) => {
  const [openAdvancedRange, setOpenAdvancedRange] = React.useState(false);
  const settingsRef = React.useRef<HTMLDivElement>(null);

  return (
    <div className="graphContainer" style={style}>
      {title && (
        <Chip class="graphChip">
          <span className="graphChipTitle">{title}</span>
        </Chip>
      )}
      {children}
      <div className="graphSettingsContainer" ref={settingsRef}>
        <FontAwesomeIcon
          icon={faGear}
          className="graphSettingsIcon"
          onClick={() => {
            setOpenAdvancedRange(!openAdvancedRange);
            if (openAdvancedRange) {
              if (!settingsRef.current) return;
              settingsRef.current.style.overflow = "hidden";
              settingsRef.current.style.width = "18px";
            } else {
              if (!settingsRef.current) return;
              settingsRef.current.style.width = "100%";
              setTimeout(() => {
                if (!settingsRef.current) return;
                settingsRef.current.style.overflow = "visible";
              }, 400);
            }
          }}
        />
        <div className="inputAdvancedRangeContainer">
          <DateRangePickerCalendar onChange={onChange} />
        </div>
      </div>
    </div>
  );
};

const GraphWithGroup: React.FC<{
  children: React.ReactNode;
  // Kept on the public API for backwards-compat callers; the visible
  // Dia/Mês/Ano toggle was removed in favour of letting the range picker
  // auto-infer granularity. Parents can still react to inferred changes by
  // wiring this — but the component itself no longer fires it.
  onChangeGroup?: (groupBy: "day" | "month" | "year") => void;
  // Granular range picker behind the gear icon. Parent infers Day/Month/Year
  // from the picked window size and re-fetches.
  onChangeRange?: (startDate: Date, endDate: Date) => void;
  groupBy?: "day" | "month" | "year";
  title?: string;
  style?: React.CSSProperties;
}> = ({ children, title, style, onChangeRange }) => {
  const [, setOpenAdvancedRange] = React.useState(false);
  const settingsRef = React.useRef<HTMLDivElement>(null);

  const toggleAdvancedRange = () => {
    setOpenAdvancedRange((prev) => {
      const next = !prev;
      if (!settingsRef.current) return next;
      if (!next) {
        settingsRef.current.style.overflow = "hidden";
        settingsRef.current.style.width = "18px";
      } else {
        settingsRef.current.style.width = "100%";
        setTimeout(() => {
          if (settingsRef.current) settingsRef.current.style.overflow = "visible";
        }, 400);
      }
      return next;
    });
  };

  return (
    <div className="graphContainer" style={style}>
      {title && (
        <Chip class="graphChip">
          <span className="graphChipTitle">{title}</span>
        </Chip>
      )}
      {children}
      {onChangeRange && (
        <div className="graphSettingsContainer" ref={settingsRef}>
          <FontAwesomeIcon
            icon={faGear}
            className="graphSettingsIcon"
            onClick={toggleAdvancedRange}
          />
          <div className="inputAdvancedRangeContainer">
            <DateRangePickerCalendar onChange={onChangeRange} />
          </div>
        </div>
      )}
    </div>
  );
};

const CustomTooltip: React.FC<{
  active?: boolean;
  payload?: any[];
  label?: string;
  format?: (value: any) => string;
}> = ({ active, payload, label, format }) => {
  if (active && payload && payload.length) {
    // Pie hover delivers a single payload entry without an axis label, so
    // fall back to the slice's own name for the header. Bar/Line charts
    // keep using the axis label.
    const headerLabel = label || (payload.length === 1 ? payload[0]?.name : "");
    const showSeriesName = payload.length > 1;
    return (
      <div
        className="customTooltipGraph"
        style={
          payload.length > 1
            ? { flexDirection: "column", borderRadius: "var(--border-radius-md)" }
            : {}
        }
      >
        {headerLabel && <span className="customTooltipGraphLabel">{capitalize(headerLabel)}:</span>}
        {payload.map((entry: any, index: number) => (
          <div key={`tooltip-${index}`} className="customTooltipGraphItem">
            {showSeriesName && (
              <>
                <span className="customTooltipGraphDot" style={{ backgroundColor: entry.color }} />
                <span className="customTooltipGraphName">{capitalize(entry.name || "")}:</span>
              </>
            )}
            <span className="customTooltipGraphValue">
              {format ? format(entry.value) : entry.value}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const CustomLegend = ({ payload }: any) => {
  return (
    <div className="graphLegendContainer">
      {payload.map((entry: any) => {
        // Recharts tags Bar entries with type "rect" and Line entries with
        // type "line". Render a rounded square for bars (matches the bar
        // corners) and a horizontal line with a centered dot for lines
        // (matches the actual line + dot marker drawn in the chart).
        const isLine = entry.type === "line";
        return (
          <div key={`${entry.dataKey}-${entry.type}`} className="graphLegendItem">
            {isLine ? (
              <svg
                className="graphLegendGlyph"
                width={22}
                height={12}
                viewBox="0 0 22 12"
                aria-hidden
              >
                <line
                  x1={0}
                  y1={6}
                  x2={22}
                  y2={6}
                  stroke={entry.color}
                  strokeWidth={2}
                  strokeLinecap="round"
                />
                <circle cx={11} cy={6} r={3.5} fill={entry.color} />
              </svg>
            ) : (
              <svg
                className="graphLegendGlyph"
                width={12}
                height={12}
                viewBox="0 0 12 12"
                aria-hidden
              >
                <rect x={0} y={0} width={12} height={12} rx={3} ry={3} fill={entry.color} />
              </svg>
            )}
            {entry.value}
          </div>
        );
      })}
    </div>
  );
};

interface GraphCostOverviewData {
  period: string;
  totalCost: number;
}

// Generate the period labels a chart should show given the current range
// and granularity. Used by ?mock=1 so the synthetic x-axis matches whatever
// the user picked in the gear menu (or the Mês/Ano preset). Backend response
// shapes follow the same convention — month labels like "fev/24" or "jul"
// when within the same year, year labels like "2024".
const MONTH_LABELS_PT = [
  "jan",
  "fev",
  "mar",
  "abr",
  "mai",
  "jun",
  "jul",
  "ago",
  "set",
  "out",
  "nov",
  "dez",
];
const MONTH_FULL_PT = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

// Last day of the month containing `d`.
function lastDayOfMonth(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}

// Renders a date range as a human-readable PT-BR title suffix. The output
// shrinks to the smallest description that still uniquely identifies the
// span — full months/years collapse to a single word, partial ranges fall
// back to numeric DD/MM/AAAA which reads cleaner than abbreviated months
// when chained ("05/04 a 20/07/2026" beats "5 abr a 20 jul/2026"). Full
// month/year cases keep the named form because "Abril de 2026" is more
// scannable than "04/2026".
function formatRangeTitle(start: Date, end: Date): string {
  if (
    !(start instanceof Date) ||
    !(end instanceof Date) ||
    isNaN(start.getTime()) ||
    isNaN(end.getTime())
  ) {
    return "";
  }
  const sd = start.getDate(),
    sm = start.getMonth(),
    sy = start.getFullYear();
  const ed = end.getDate(),
    em = end.getMonth(),
    ey = end.getFullYear();
  const pad = (n: number) => String(n).padStart(2, "0");

  // Friendly presets — checked before any numeric formatting so that the
  // common cases ("Hoje", "Mês Atual", etc.) win even when they also
  // satisfy a more generic rule (e.g. last day of the month also
  // qualifies as "whole calendar month"). Order matters: smaller scope
  // first, so Hoje > Esta Semana > Mês Atual > Ano Atual.
  const today = new Date();
  const ty = today.getFullYear(),
    tm = today.getMonth(),
    td = today.getDate();
  const isSameYMD = (d: Date, y: number, m: number, day: number) =>
    d.getFullYear() === y && d.getMonth() === m && d.getDate() === day;

  // Hoje — single-day range matching today.
  if (isSameYMD(start, ty, tm, td) && isSameYMD(end, ty, tm, td)) {
    return "Hoje";
  }
  // Ontem — single-day range matching yesterday (handles month/year wraps via Date math).
  const yest = new Date(ty, tm, td - 1);
  if (
    isSameYMD(start, yest.getFullYear(), yest.getMonth(), yest.getDate()) &&
    isSameYMD(end, yest.getFullYear(), yest.getMonth(), yest.getDate())
  ) {
    return "Ontem";
  }
  // Esta Semana — Sunday of current week → today (PT-BR week starts Sunday).
  const weekStart = new Date(ty, tm, td - today.getDay());
  if (
    isSameYMD(start, weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate()) &&
    isSameYMD(end, ty, tm, td)
  ) {
    return "Esta Semana";
  }
  // Últimos 7 dias — rolling 7-day window ending today (today − 6 → today).
  const last7Start = new Date(ty, tm, td - 6);
  if (
    isSameYMD(start, last7Start.getFullYear(), last7Start.getMonth(), last7Start.getDate()) &&
    isSameYMD(end, ty, tm, td)
  ) {
    return "Últimos 7 dias";
  }
  // Mês Atual — 1st of current month → today.
  if (sd === 1 && sy === ty && sm === tm && isSameYMD(end, ty, tm, td)) {
    return "Mês Atual";
  }
  // Últimos 30 dias — rolling 30-day window ending today. Matches what
  // `defaultInitialDateByGroup("day")` returns (today − 29 → today), so the
  // "day" preset collapses to this label.
  const last30Start = new Date(ty, tm, td - 29);
  if (
    isSameYMD(start, last30Start.getFullYear(), last30Start.getMonth(), last30Start.getDate()) &&
    isSameYMD(end, ty, tm, td)
  ) {
    return "Últimos 30 dias";
  }
  // Últimos 12 meses — rolling 12-month window ending today. Anchors at
  // the 1st of the month 11 months back, matching what
  // `defaultInitialDateByGroup("month")` returns, so the "month" preset
  // collapses to this label.
  const last12Start = new Date(ty, tm - 11, 1);
  if (
    isSameYMD(start, last12Start.getFullYear(), last12Start.getMonth(), last12Start.getDate()) &&
    isSameYMD(end, ty, tm, td)
  ) {
    return "Últimos 12 meses";
  }
  // Ano Atual — Jan 1 of current year → today.
  if (sd === 1 && sm === 0 && sy === ty && isSameYMD(end, ty, tm, td)) {
    return "Ano Atual";
  }
  // Whole calendar year (Jan 1 → Dec 31, same year).
  if (sm === 0 && sd === 1 && em === 11 && ed === 31 && sy === ey) {
    return String(sy);
  }
  // Whole calendar month (1st → last day of same month).
  if (sd === 1 && ed === lastDayOfMonth(end) && sm === em && sy === ey) {
    return `${MONTH_FULL_PT[sm]} de ${sy}`;
  }
  // Single day.
  if (sy === ey && sm === em && sd === ed) {
    return `${pad(sd)}/${pad(sm + 1)}/${sy}`;
  }
  // Same month, partial range — collapse the shared month/year suffix.
  if (sy === ey && sm === em) {
    return `${pad(sd)} a ${pad(ed)}/${pad(sm + 1)}/${sy}`;
  }
  // Same year, cross-month — keep the year only at the end.
  if (sy === ey) {
    return `${pad(sd)}/${pad(sm + 1)} a ${pad(ed)}/${pad(em + 1)}/${sy}`;
  }
  // Cross-year — full DD/MM/AAAA on both sides.
  return `${pad(sd)}/${pad(sm + 1)}/${sy} a ${pad(ed)}/${pad(em + 1)}/${ey}`;
}

function buildMockPeriods(start: Date, end: Date, groupBy: "day" | "month" | "year"): string[] {
  if (
    !(start instanceof Date) ||
    !(end instanceof Date) ||
    isNaN(start.getTime()) ||
    isNaN(end.getTime())
  ) {
    return [];
  }
  if (start > end) return [];
  const periods: string[] = [];
  if (groupBy === "year") {
    for (let y = start.getFullYear(); y <= end.getFullYear(); y++) {
      periods.push(String(y));
    }
    return periods;
  }
  if (groupBy === "day") {
    const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    const sameMonth =
      start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth();
    while (cursor <= last) {
      const dd = String(cursor.getDate()).padStart(2, "0");
      const mm = MONTH_LABELS_PT[cursor.getMonth()];
      periods.push(sameMonth ? dd : `${dd}/${mm}`);
      cursor.setDate(cursor.getDate() + 1);
    }
    return periods;
  }
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  const sameYear = start.getFullYear() === end.getFullYear();
  while (cursor <= last) {
    const m = MONTH_LABELS_PT[cursor.getMonth()];
    periods.push(sameYear ? m : `${m}/${String(cursor.getFullYear()).slice(-2)}`);
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return periods;
}

function defaultInitialDateByGroup(groupBy: string): {
  startDate: Date;
} {
  const now = new Date();

  if (groupBy === "day") {
    // Last 30 days — gives the daily view enough density without overflowing
    // the chart. The granular range picker (gear) lets the user override.
    const start = new Date(now);
    start.setDate(start.getDate() - 29);
    start.setHours(0, 0, 0, 0);
    return { startDate: start };
  }

  if (groupBy === "month") {
    return {
      startDate: new Date(now.getFullYear(), now.getMonth() - 11, 1),
    };
  }

  return {
    startDate: new Date(now.getFullYear() - 11, 0, 1),
  };
}

const GraphCostOverview: React.FC<RangeWithGroup> = ({
  startDate,
  endDate,
  groupBy,
  refreshRevision,
}) => {
  const now = new Date();
  const [loading, setLoading] = React.useState(true);
  // Distinguishes "the call failed" from "the period genuinely had no costs".
  // Without it a 500/network error renders identically to a real zero.
  const [loadFailed, setLoadFailed] = React.useState(false);
  const [groupByState, setGroupByState] = React.useState<"day" | "month" | "year">(
    groupBy || "month",
  );
  const [startDateState, setStartDateState] = React.useState<Date>(
    startDate ? new Date(startDate) : defaultInitialDateByGroup(groupBy || "month").startDate,
  );
  const [endDateState, setEndDateState] = React.useState<Date>(endDate ? new Date(endDate) : now);
  const [data, setData] = React.useState<GraphCostOverviewData[]>([]);

  class API {
    static async fetchCostOverview(isCancelled: () => boolean = () => false) {
      try {
        const JWT = (await localforage.getItem("JWT")) as string | undefined;
        if (!JWT) throw new Error("JWT unavailable");

        const headers: { [key: string]: string | undefined } = {
          "X-JWT": JWT,
        };

        const config: AxiosRequestConfig<any> = {
          params: { ...dashboardRangeParams(startDateState, endDateState), groupBy: groupByState },
          headers,
        };

        const res = await paymentAPI.get("/dashboard/costs/overview", config);
        // Drop the response if a newer range was picked while this request was
        // in flight — otherwise a slow earlier fetch can resolve last and
        // overwrite the chart with stale data.
        if (isCancelled()) return;
        setData(parseGrossBillableOverview(res.data));
      } catch (error) {
        if (isCancelled()) return;
        setLoadFailed(true);
        console.error("Error fetching cost overview data:", error);
      }
    }
  }

  React.useEffect(() => {
    if (
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("mock") === "1"
    ) {
      const r = (min: number, max: number) => +(min + Math.random() * (max - min)).toFixed(2);
      setData(
        buildMockPeriods(startDateState, endDateState, groupByState).map((period) => ({
          period,
          totalCost: r(180, 1850),
        })),
      );
      setLoadFailed(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoadFailed(false);
    setLoading(true);
    API.fetchCostOverview(() => cancelled).then(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [startDateState, endDateState, groupByState, refreshRevision]);

  // Stats used to drive the visual flourishes (avg reference line + max
  // highlight). Computed once per data update — recharts re-renders the
  // chart anyway when data changes.
  const avgCost = React.useMemo(() => {
    if (!data.length) return 0;
    const sum = data.reduce((acc, d) => acc + (Number.isFinite(d.totalCost) ? d.totalCost : 0), 0);
    return sum / data.length;
  }, [data]);
  const maxCost = React.useMemo(() => {
    if (!data.length) return 0;
    return data.reduce((m, d) => (d.totalCost > m ? d.totalCost : m), -Infinity);
  }, [data]);
  const formatCurrency = formatCurrencyBRL;
  const graphState = resolveFinancialGraphState(loading, loadFailed, data.length > 0);

  return (
    <GraphWithGroup
      title={`Custos faturáveis brutos - ${formatRangeTitle(startDateState, endDateState)}`}
      groupBy={groupByState}
      onChangeGroup={(gb) => {
        const initialDates = defaultInitialDateByGroup(gb);
        setEndDateState(now);
        setStartDateState(initialDates.startDate);
        setGroupByState(gb);
      }}
      onChangeRange={(start, end) => {
        setStartDateState(start);
        setEndDateState(end);
        // Auto-infer granularity from the picked range so the x-axis density
        // stays useful: <30d → day, <2y → month, ≥2y → year. Manual toggle
        // (Dia/Mês/Ano) still works to override.
        const diffDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
        const inferred: "day" | "month" | "year" =
          diffDays < 30 ? "day" : diffDays < 365 * 2 ? "month" : "year";
        if (inferred !== groupByState) setGroupByState(inferred);
      }}
      style={{ minWidth: "400px" }}
    >
      {graphState === "loading" ? (
        <Skeleton
          variant="rounded"
          sx={{ minHeight: "280px", bgcolor: "#99999925" }}
          animation="wave"
        />
      ) : graphState === "error" ? (
        <NoDataGraph
          label="Não foi possível carregar os custos"
          icon={<ErrorOutlineRounded className="noDataGraphIcon" />}
        />
      ) : graphState === "empty" ? (
        <NoDataGraph
          label="Sem custos no período"
          icon={<BarChartRounded className="noDataGraphIcon" />}
        />
      ) : (
        <AreaChart
          className="barChartContainer"
          style={{ width: "100%", height: "calc(100% - 30px)" }}
          data={data}
          margin={{ top: 16, right: 16, bottom: 0, left: 0 }}
        >
          {/* Vertical gradient under the line — saturated near the curve,
              fading to transparent at the baseline. Same idea as the bar
              gradient, just rotated for the area metaphor. */}
          <defs>
            <linearGradient id="costAreaGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--brand-9)" stopOpacity={0.5} />
              <stop offset="100%" stopColor="var(--brand-5)" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 6"
            stroke="var(--brand-6)"
            strokeOpacity={0.4}
            horizontal
            vertical
            // Sync the vertical guideline coordinates with the ticks the
            // XAxis actually renders (interval="preserveStartEnd" can skip
            // some). recharts hands us the resolved xAxis.ticks array — each
            // entry already lines up with a visible tick, so each label gets
            // its own dashed guideline.
            verticalCoordinatesGenerator={({ xAxis }: any) =>
              ((xAxis?.ticks as any[]) ?? [])
                .map((t) => t?.coordinate ?? t?.tickCoord)
                .filter((c): c is number => typeof c === "number")
            }
          />
          <XAxis
            dataKey="period"
            stroke="var(--brand-12)"
            tickFormatter={(value) => capitalize(value)}
            tickLine={false}
            axisLine={{ stroke: "var(--brand-6)" }}
            tick={{ fontSize: 12, fill: "var(--brand-12)" }}
            interval="preserveStartEnd"
            minTickGap={24}
            tickMargin={10}
            height={36}
          />
          <YAxis
            stroke="var(--brand-12)"
            tickFormatter={formatCurrency}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 12, fill: "var(--brand-12)" }}
            width={80}
          />
          <Tooltip
            cursor={{ stroke: "var(--brand-9)", strokeDasharray: "3 4" }}
            content={<CustomTooltip format={formatCurrency} />}
          />
          {/* Reference line at the period average. Helps the eye answer
              "is this month above or below my normal" without doing math. */}
          {avgCost > 0 && (
            <ReferenceLine
              y={avgCost}
              stroke="var(--brand-11)"
              strokeOpacity={0.6}
              strokeDasharray="4 4"
              ifOverflow="extendDomain"
              label={{
                value: `Média ${formatCurrency(avgCost)}`,
                position: "right",
                fill: "var(--brand-11)",
                fontSize: 11,
                offset: 6,
              }}
            />
          )}
          <Area
            type="monotone"
            dataKey="totalCost"
            stroke="var(--brand-9)"
            strokeWidth={2.5}
            fill="url(#costAreaGradient)"
            dot={{ r: 3, fill: "var(--brand-9)", stroke: "var(--brand-2)", strokeWidth: 1 }}
            activeDot={{ r: 6, fill: "var(--brand-9)", stroke: "var(--brand-2)", strokeWidth: 2 }}
            isAnimationActive
            animationDuration={650}
          />
          {/* Spotlight the max point — single ReferenceDot in the accent
              color stays consistent with the previous bar highlight without
              cluttering every dot. */}
          {maxCost > 0 &&
            (() => {
              const maxEntry = data.find((d) => d.totalCost === maxCost);
              if (!maxEntry) return null;
              return (
                <ReferenceDot
                  x={maxEntry.period}
                  y={maxCost}
                  r={6}
                  fill="var(--accent-primary-9)"
                  stroke="var(--brand-2)"
                  strokeWidth={2}
                  ifOverflow="extendDomain"
                />
              );
            })()}
        </AreaChart>
      )}
    </GraphWithGroup>
  );
};

const GraphCostBreakdown: React.FC<BasicRangeDate> = ({ startDate, endDate, refreshRevision }) => {
  const now = new Date();
  const [loading, setLoading] = React.useState(true);
  // Distinguishes "the call failed" from "the period genuinely had no costs".
  const [loadFailed, setLoadFailed] = React.useState(false);
  const [startDateState, setStartDateState] = React.useState<Date>(
    startDate ? new Date(startDate) : new Date(now.getFullYear(), now.getMonth(), 1),
  );
  const [endDateState, setEndDateState] = React.useState<Date>(endDate ? new Date(endDate) : now);
  const [itemConst, setItemConst] = React.useState<{ name: string; value: number }[]>([]);
  const [totalCost, setTotalCost] = React.useState<number>(0);

  class API {
    static async fetchCostOverview(isCancelled: () => boolean = () => false) {
      try {
        const JWT = (await localforage.getItem("JWT")) as string | undefined;
        if (!JWT) throw new Error("JWT unavailable");

        const headers: { [key: string]: string | undefined } = {
          "X-JWT": JWT,
        };

        const config: AxiosRequestConfig<any> = {
          params: dashboardRangeParams(startDateState, endDateState),
          headers,
        };

        const res = await paymentAPI.get("/dashboard/costs/breakdown", config);
        // Drop the response if a newer range was picked while this request was
        // in flight — otherwise a slow earlier fetch can resolve last and
        // overwrite the chart with stale data.
        if (isCancelled()) return;
        const breakdown = buildCostBreakdown(parseGrossBillableBreakdown(res.data));
        setItemConst(breakdown.items);
        setTotalCost(breakdown.total);
      } catch (error) {
        if (isCancelled()) return;
        setLoadFailed(true);
        console.error("Error fetching cost breakdown data:", error);
      }
    }
  }

  React.useEffect(() => {
    if (
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("mock") === "1"
    ) {
      const r = (min: number, max: number) => +(min + Math.random() * (max - min)).toFixed(2);
      const access = r(120, 480);
      const base = r(80, 240);
      const view = r(200, 600);
      const negotiate = r(0, 300);
      const breakdown = buildCostBreakdown({ access, base, view, negotiate });
      setItemConst(breakdown.items);
      setTotalCost(breakdown.total);
      setLoadFailed(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoadFailed(false);
    setLoading(true);
    API.fetchCostOverview(() => cancelled).then(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [startDateState, endDateState, refreshRevision]);

  const COLORS = [
    "var(--accent-primary-9)",
    "var(--accent-secondary-9)",
    "var(--accent-tertiary-9)",
    "var(--success-9)",
  ];
  // Soft variant of each color for the radial gradient base — gives the
  // donut depth without darkening the slice past readable.
  const COLORS_SOFT = [
    "var(--accent-primary-7)",
    "var(--accent-secondary-7)",
    "var(--accent-tertiary-7)",
    "var(--success-7)",
  ];

  const formatCurrency = formatCurrencyBRL;
  const graphState = resolveFinancialGraphState(loading, loadFailed, totalCost !== 0);

  // Compact form for the donut center label only — keeps the largest possible
  // text from kissing the inner ring at very high totals. Suffixes follow the
  // PT-BR convention used in financial UIs: "mil" / "Mi" / "Bi" instead of
  // the American "K / M / B". Tooltip continues to show the full precise value.
  const formatCurrencyCompact = (value: number) => {
    const abs = Math.abs(value);
    const sign = value < 0 ? "-" : "";
    const fmt = (v: number) =>
      v
        .toFixed(v < 10 ? 1 : 0)
        .replace(".", ",")
        .replace(/,0+$/, "");
    if (abs >= 1_000_000_000) return `${sign}R$ ${fmt(abs / 1_000_000_000)} Bi`;
    if (abs >= 1_000_000) return `${sign}R$ ${fmt(abs / 1_000_000)} Mi`;
    if (abs >= 10_000) return `${sign}R$ ${fmt(abs / 1_000)} mil`;
    return formatCurrency(value);
  };

  return (
    <Graph
      title={`Detalhamento do faturamento bruto - ${formatRangeTitle(startDateState, endDateState)}`}
      onChange={(sd, ed) => {
        setStartDateState(sd);
        setEndDateState(ed);
      }}
      style={{ minWidth: "300px", maxWidth: "300px" }}
    >
      {graphState === "loading" ? (
        <Skeleton
          variant="rounded"
          sx={{ minHeight: "280px", bgcolor: "#99999925" }}
          animation="wave"
        />
      ) : graphState === "error" ? (
        <NoDataGraph
          label="Não foi possível carregar os custos"
          icon={<ErrorOutlineRounded className="noDataGraphIcon" />}
        />
      ) : graphState === "empty" ? (
        <NoDataGraph
          label="Sem custos no período"
          icon={<DataUsageRounded className="noDataGraphIcon" />}
        />
      ) : (
        <PieChart
          className="pieChartContainer"
          style={{
            width: "100%",
            height: "calc(100% - 10px)",
            aspectRatio: 1,
          }}
          responsive
        >
          {/* Per-slice radial gradients: outer ring saturated, inner faded —
              donut ganha leve volumetria sem precisar de drop shadow. */}
          <defs>
            {itemConst.map((_, idx) => (
              <radialGradient
                key={`pieGrad-${idx}`}
                id={`pieGrad-${idx}`}
                cx="50%"
                cy="50%"
                r="50%"
              >
                <stop
                  offset="50%"
                  stopColor={COLORS_SOFT[idx % COLORS_SOFT.length]}
                  stopOpacity={0.85}
                />
                <stop offset="100%" stopColor={COLORS[idx % COLORS.length]} stopOpacity={1} />
              </radialGradient>
            ))}
          </defs>
          <Pie
            data={itemConst.map(({ name, value }) => ({
              name: name.charAt(0).toUpperCase() + name.slice(1),
              value,
            }))}
            cx="50%"
            cy="50%"
            // Outer labels removed (they collided with the donut at higher
            // values like R$ 10k+). Donut occupies more of the panel now;
            // per-slice info shows up in the Tooltip on hover.
            innerRadius="68%"
            outerRadius="96%"
            paddingAngle={2}
            cornerRadius={2}
            stroke="none"
            isAnimationActive
            animationDuration={650}
            shapeRendering="geometricPrecision"
            labelLine={false}
            label={false}
          >
            {itemConst.map((_, index) => (
              <Cell key={`cell-${index}`} fill={`url(#pieGrad-${index})`} stroke="none" />
            ))}
            {/* Two stacked center labels — recharts renders <Label position="center">
                inside a <Pie> by anchoring at the donut's geometric center, so
                using two with vertical dy offsets gives an "eyebrow + value"
                pair without needing a custom content function. */}
            <Label
              position="center"
              dy={-10}
              value="TOTAL"
              fill="var(--brand-11)"
              style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.8px" }}
            />
            <Label
              position="center"
              dy={10}
              value={formatCurrencyCompact(totalCost)}
              fill="var(--brand-12)"
              style={{ fontSize: 18, fontWeight: 600 }}
            />
          </Pie>
          <Tooltip content={<CustomTooltip format={formatCurrency} />} />
          <Legend content={<CustomLegend />} />
        </PieChart>
      )}
    </Graph>
  );
};

interface GraphAccessAndViewByFeatureData {
  period: string;
  viewHall: number;
  viewRadio: number;
  viewTv: number;
  accessHall: number;
  accessRadio: number;
  accessTv: number;
}

// Shared hook: each chart panel keeps its own range/groupBy state so the user
// can scope Visualizações and Cliques independently. The fetch (or mock fill)
// logic is identical between the two, so we extract it here to avoid drift.
function useAccessAndViewData(
  startDate: Date,
  endDate: Date,
  groupBy: "day" | "month" | "year",
  refreshRevision?: number,
) {
  const [loading, setLoading] = React.useState(true);
  const [loadFailed, setLoadFailed] = React.useState(false);
  const [data, setData] = React.useState<GraphAccessAndViewByFeatureData[]>([]);

  React.useEffect(() => {
    if (
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("mock") === "1"
    ) {
      const r = (min: number, max: number) => Math.round(min + Math.random() * (max - min));
      setData(
        buildMockPeriods(startDate, endDate, groupBy).map((period) => ({
          period,
          viewHall: r(800, 3200),
          viewRadio: r(600, 2400),
          viewTv: r(900, 3600),
          accessHall: r(20, 120),
          accessRadio: r(15, 90),
          accessTv: r(25, 140),
        })),
      );
      setLoadFailed(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadFailed(false);
      const JWT = (await localforage.getItem("JWT")) as string | undefined;
      if (!JWT) {
        if (!cancelled) {
          setLoadFailed(true);
          setLoading(false);
        }
        return;
      }
      try {
        const res = await paymentAPI.get("/dashboard/by-feature", {
          params: { ...dashboardRangeParams(startDate, endDate), groupBy },
          headers: { "X-JWT": JWT },
        });
        if (!cancelled) setData(res.data || []);
      } catch (error) {
        if (!cancelled) setLoadFailed(true);
        console.error("Error fetching access and view data:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, groupBy, refreshRevision]);

  return { data, loading, loadFailed };
}

const ACCESS_VIEW_COLORS = {
  viewHall: "var(--accent-primary-6)",
  viewRadio: "var(--accent-secondary-6)",
  viewTV: "var(--accent-tertiary-6)",
  accessHall: "var(--accent-primary-8)",
  accessRadio: "var(--accent-secondary-8)",
  accessTV: "var(--accent-tertiary-8)",
};

const GraphViewsByFeature: React.FC<RangeWithGroup> = ({
  startDate,
  endDate,
  groupBy,
  refreshRevision,
}) => {
  const now = new Date();
  const [groupByState, setGroupByState] = React.useState<"day" | "month" | "year">(
    groupBy || "month",
  );
  const [startDateState, setStartDateState] = React.useState<Date>(
    startDate ? new Date(startDate) : defaultInitialDateByGroup(groupBy || "month").startDate,
  );
  const [endDateState, setEndDateState] = React.useState<Date>(endDate ? new Date(endDate) : now);
  const { data, loading, loadFailed } = useAccessAndViewData(
    startDateState,
    endDateState,
    groupByState,
    refreshRevision,
  );

  return (
    <GraphWithGroup
      title={`Visualizações - ${formatRangeTitle(startDateState, endDateState)}`}
      groupBy={groupByState}
      onChangeGroup={(gb) => {
        const initialDates = defaultInitialDateByGroup(gb);
        setEndDateState(now);
        setStartDateState(initialDates.startDate);
        setGroupByState(gb);
      }}
      onChangeRange={(start, end) => {
        setStartDateState(start);
        setEndDateState(end);
        const diffDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
        const inferred: "day" | "month" | "year" =
          diffDays < 30 ? "day" : diffDays < 365 * 2 ? "month" : "year";
        if (inferred !== groupByState) setGroupByState(inferred);
      }}
      style={{ minWidth: "500px", flexGrow: 1 }}
    >
      {loading ? (
        <Skeleton
          variant="rounded"
          sx={{ minHeight: "250px", bgcolor: "#99999925" }}
          animation="wave"
        />
      ) : loadFailed ? (
        <NoDataGraph
          label="Não foi possível carregar as visualizações"
          icon={<ErrorOutlineRounded className="noDataGraphIcon" />}
        />
      ) : data.length === 0 ? (
        <NoDataGraph
          label="Sem visualizações no período"
          icon={<AutoGraphRounded className="noDataGraphIcon" />}
        />
      ) : (
        <AreaChart
          className="barChartContainer"
          style={{ width: "100%", height: "calc(100% - 30px)" }}
          data={data}
          margin={{ top: 16, right: 16, bottom: 0, left: 0 }}
        >
          <CartesianGrid
            strokeDasharray="3 6"
            stroke="var(--brand-6)"
            strokeOpacity={0.4}
            horizontal
            vertical
            verticalCoordinatesGenerator={({ xAxis }: any) =>
              ((xAxis?.ticks as any[]) ?? [])
                .map((t) => t?.coordinate ?? t?.tickCoord)
                .filter((c): c is number => typeof c === "number")
            }
          />
          <XAxis
            dataKey="period"
            stroke="var(--brand-12)"
            tickFormatter={(value) => capitalize(value)}
            tick={{ fontSize: 12, fill: "var(--brand-12)" }}
            tickLine={false}
            axisLine={{ stroke: "var(--brand-6)" }}
            interval="preserveStartEnd"
            minTickGap={24}
            tickMargin={10}
            height={36}
          />
          <YAxis
            stroke="var(--brand-12)"
            tick={{ fontSize: 12, fill: "var(--brand-12)" }}
            tickLine={false}
            axisLine={false}
            width={56}
          />
          <Tooltip
            cursor={{ stroke: "var(--brand-9)", strokeDasharray: "3 4" }}
            content={<CustomTooltip />}
          />
          <Legend content={<CustomLegend />} />
          <Area
            stackId="views"
            type="monotone"
            dataKey="viewHall"
            name="Átrio"
            stroke={ACCESS_VIEW_COLORS.viewHall}
            fill={ACCESS_VIEW_COLORS.viewHall}
            fillOpacity={0.45}
            strokeWidth={1.5}
            legendType="rect"
            isAnimationActive
            animationDuration={650}
          />
          <Area
            stackId="views"
            type="monotone"
            dataKey="viewRadio"
            name="Rádio"
            stroke={ACCESS_VIEW_COLORS.viewRadio}
            fill={ACCESS_VIEW_COLORS.viewRadio}
            fillOpacity={0.45}
            strokeWidth={1.5}
            legendType="rect"
            isAnimationActive
            animationDuration={650}
          />
          <Area
            stackId="views"
            type="monotone"
            dataKey="viewTv"
            name="TV"
            stroke={ACCESS_VIEW_COLORS.viewTV}
            fill={ACCESS_VIEW_COLORS.viewTV}
            fillOpacity={0.45}
            strokeWidth={1.5}
            legendType="rect"
            isAnimationActive
            animationDuration={650}
          />
        </AreaChart>
      )}
    </GraphWithGroup>
  );
};

const GraphClicksByFeature: React.FC<RangeWithGroup> = ({
  startDate,
  endDate,
  groupBy,
  refreshRevision,
}) => {
  const now = new Date();
  const [groupByState, setGroupByState] = React.useState<"day" | "month" | "year">(
    groupBy || "month",
  );
  const [startDateState, setStartDateState] = React.useState<Date>(
    startDate ? new Date(startDate) : defaultInitialDateByGroup(groupBy || "month").startDate,
  );
  const [endDateState, setEndDateState] = React.useState<Date>(endDate ? new Date(endDate) : now);
  const { data, loading, loadFailed } = useAccessAndViewData(
    startDateState,
    endDateState,
    groupByState,
    refreshRevision,
  );

  return (
    <GraphWithGroup
      title={`Cliques - ${formatRangeTitle(startDateState, endDateState)}`}
      groupBy={groupByState}
      onChangeGroup={(gb) => {
        const initialDates = defaultInitialDateByGroup(gb);
        setEndDateState(now);
        setStartDateState(initialDates.startDate);
        setGroupByState(gb);
      }}
      onChangeRange={(start, end) => {
        setStartDateState(start);
        setEndDateState(end);
        const diffDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
        const inferred: "day" | "month" | "year" =
          diffDays < 30 ? "day" : diffDays < 365 * 2 ? "month" : "year";
        if (inferred !== groupByState) setGroupByState(inferred);
      }}
      style={{ minWidth: "500px", flexGrow: 1 }}
    >
      {loading ? (
        <Skeleton
          variant="rounded"
          sx={{ minHeight: "250px", bgcolor: "#99999925" }}
          animation="wave"
        />
      ) : loadFailed ? (
        <NoDataGraph
          label="Não foi possível carregar os cliques"
          icon={<ErrorOutlineRounded className="noDataGraphIcon" />}
        />
      ) : data.length === 0 ? (
        <NoDataGraph
          label="Sem cliques no período"
          icon={<AutoGraphRounded className="noDataGraphIcon" />}
        />
      ) : (
        // BarChart agrupado (sem stack) — 3 barras lado-a-lado por período.
        // Cliques são eventos discretos por feature; barras facilitam a
        // comparação direta "quem teve mais cliques nesse mês". Visualmente
        // contrasta com o AreaChart suave de Visualizações.
        <BarChart
          className="barChartContainer"
          style={{ width: "100%", height: "calc(100% - 30px)" }}
          data={data}
          margin={{ top: 16, right: 16, bottom: 0, left: 0 }}
          barCategoryGap="35%"
        >
          <defs>
            <linearGradient id="clicksHallGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={ACCESS_VIEW_COLORS.accessHall} stopOpacity={0.95} />
              <stop offset="100%" stopColor={ACCESS_VIEW_COLORS.accessHall} stopOpacity={0.55} />
            </linearGradient>
            <linearGradient id="clicksRadioGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={ACCESS_VIEW_COLORS.accessRadio} stopOpacity={0.95} />
              <stop offset="100%" stopColor={ACCESS_VIEW_COLORS.accessRadio} stopOpacity={0.55} />
            </linearGradient>
            <linearGradient id="clicksTvGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={ACCESS_VIEW_COLORS.accessTV} stopOpacity={0.95} />
              <stop offset="100%" stopColor={ACCESS_VIEW_COLORS.accessTV} stopOpacity={0.55} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 6"
            stroke="var(--brand-6)"
            strokeOpacity={0.4}
            horizontal
            vertical={false}
          />
          <XAxis
            dataKey="period"
            stroke="var(--brand-12)"
            tickFormatter={(value) => capitalize(value)}
            tick={{ fontSize: 12, fill: "var(--brand-12)" }}
            tickLine={false}
            axisLine={{ stroke: "var(--brand-6)" }}
            interval="preserveStartEnd"
            minTickGap={24}
            tickMargin={10}
            height={36}
          />
          <YAxis
            stroke="var(--brand-12)"
            tick={{ fontSize: 12, fill: "var(--brand-12)" }}
            tickLine={false}
            axisLine={false}
            width={56}
          />
          <Tooltip cursor={{ fill: "var(--brand-3)" }} content={<CustomTooltip />} />
          <Legend content={<CustomLegend />} />
          <Bar
            stackId="clicks"
            dataKey="accessHall"
            name="Átrio"
            fill="url(#clicksHallGradient)"
            isAnimationActive
            animationDuration={650}
          />
          <Bar
            stackId="clicks"
            dataKey="accessRadio"
            name="Rádio"
            fill="url(#clicksRadioGradient)"
            isAnimationActive
            animationDuration={650}
          />
          <Bar
            stackId="clicks"
            dataKey="accessTv"
            name="TV"
            fill="url(#clicksTvGradient)"
            radius={[6, 6, 0, 0]}
            isAnimationActive
            animationDuration={650}
          />
        </BarChart>
      )}
    </GraphWithGroup>
  );
};

export const PainelPage: React.FC = () => {
  const { amaryCourse } = useUser();
  const [refreshRevision, setRefreshRevision] = React.useState(0);
  const refreshDeduper = React.useRef(new DashboardRefreshDeduper());

  React.useEffect(() => {
    let cancelled = false;
    let eventName = "";
    let debounceTimer: number | undefined;

    const scheduleRefresh = (payload?: DashboardInvalidation) => {
      if (!refreshDeduper.current.shouldRefresh(payload)) return;
      if (debounceTimer !== undefined) window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        if (!cancelled) setRefreshRevision((current) => current + 1);
      }, 150);
    };

    void sessionInfo().then((info) => {
      if (cancelled || !info?.org_id) return;
      eventName = `dashboardCostsChanged-${info.org_id}`;
      RTA.connect();
      RTA.on(eventName, scheduleRefresh);
    });

    // RTA is a low-latency hint, not the financial source of truth. Re-read on
    // reconnect/focus and periodically so a stream outage cannot freeze the
    // projection indefinitely; the HTTP responses remain org-authorized.
    const unsubscribeReconnect = onRTAReconnect(() => scheduleRefresh());
    const handleFocus = () => scheduleRefresh();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") scheduleRefresh();
    };
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);
    const poll = window.setInterval(() => scheduleRefresh(), 60_000);

    return () => {
      cancelled = true;
      if (eventName) RTA.off(eventName, scheduleRefresh);
      unsubscribeReconnect();
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.clearInterval(poll);
      if (debounceTimer !== undefined) window.clearTimeout(debounceTimer);
    };
  }, []);

  if (amaryCourse) {
    return <PainelPageAmary />;
  }

  return (
    <div id="painelPageContainer">
      <Title
        title="Painel"
        subtitle="Acompanhe desempenho e custos brutos faturáveis. Registros anteriores preservam líquido do provedor, residual Enterprise e caixa recebido separadamente."
      />
      <div className="graphsContainer">
        <GraphCostOverview refreshRevision={refreshRevision} />
        <GraphCostBreakdown refreshRevision={refreshRevision} />
      </div>
      <div className="graphsContainer">
        <GraphViewsByFeature refreshRevision={refreshRevision} />
        <GraphClicksByFeature refreshRevision={refreshRevision} />
      </div>
    </div>
  );
};
